import React, { useEffect, useMemo, useState } from 'react';
import {
  SystemAdminDataService, PricingTierVersion, NewOrganisationResult,
} from '../../services/systemAdminDataService';

interface Props {
  show: boolean;
  onClose: () => void;
  /** Called after the organisation is created, with what happened. */
  onCreated: (result: NewOrganisationResult, name: string, adminEmail: string) => void;
}

/** 'Oakfield Primary (East)' → 'oakfield-primary-east' */
function toSlug(name: string): string {
  return name.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
}

/**
 * System Admin → Organisations → New organisation. Creates the organisation and
 * its first admin in one step (tdp-api POST /system-admin/organisations): the
 * admin gets a Cognito invitation with a temporary password, or — if they
 * already have a Rustle It login — keeps it and sees the new organisation next
 * time they sign in.
 */
const NewOrganisationModal: React.FC<Props> = ({ show, onClose, onCreated }) => {
  const [tiers, setTiers] = useState<PricingTierVersion[]>([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [email, setEmail] = useState('');
  const [tierCode, setTierCode] = useState('');
  const [status, setStatus] = useState<'trial' | 'active'>('trial');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setName(''); setSlug(''); setSlugEdited(false); setEmail('');
    setStatus('trial'); setBillingPeriod('monthly'); setAdminName(''); setAdminEmail(''); setError(null);
    SystemAdminDataService.fetchPricing()
      .then(versions => {
        // Tiers as they stand today, in price-book order, sellable only.
        const current = versions.filter(v => v.isCurrent && v.isSellable).sort((a, b) => a.sortOrder - b.sortOrder);
        setTiers(current);
        setTierCode(current[0]?.code ?? '');
      })
      .catch(() => setError('Could not load the plan tiers.'));
  }, [show]);

  // The slug follows the name until it is edited by hand.
  useEffect(() => { if (!slugEdited) setSlug(toSlug(name)); }, [name, slugEdited]);

  const missing = useMemo(() => {
    if (name.trim().length < 2) return 'Enter the organisation name';
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) || slug.length < 2) return 'Enter a slug: lowercase letters, numbers and hyphens';
    if (!tierCode) return 'Choose a plan tier';
    if (adminName.trim().length < 2) return "Enter the admin's name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim())) return "Enter the admin's email";
    return null;
  }, [name, slug, tierCode, adminName, adminEmail]);

  if (!show) return null;

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await SystemAdminDataService.createOrganisation({
        name: name.trim(),
        slug,
        email: email.trim() || undefined,
        tierCode,
        status,
        billingPeriod,
        admin: { name: adminName.trim(), email: adminEmail.trim() },
      });
      onCreated(result, name.trim(), adminEmail.trim());
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not create the organisation.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="new-org-title" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="new-org-title">New organisation</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={saving} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}

            <h6 className="text-muted text-uppercase small mb-2">Organisation</h6>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label" htmlFor="no-name">Name</label>
                <input id="no-name" className="form-control" maxLength={200} value={name} disabled={saving}
                  onChange={e => setName(e.target.value)} placeholder="e.g. Oakfield Primary" />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="no-slug">Slug</label>
                <input id="no-slug" className="form-control" maxLength={100} value={slug} disabled={saving}
                  onChange={e => { setSlug(e.target.value.toLowerCase()); setSlugEdited(true); }} />
                <div className="form-text">Unique, permanent: used in storage paths. Lowercase, numbers, hyphens.</div>
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="no-email">Contact email (optional)</label>
                <input id="no-email" type="email" className="form-control" value={email} disabled={saving}
                  onChange={e => setEmail(e.target.value)} />
              </div>
            </div>

            <h6 className="text-muted text-uppercase small mb-2">Plan</h6>
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label" htmlFor="no-tier">Tier</label>
                <select id="no-tier" className="form-select" value={tierCode} disabled={saving} onChange={e => setTierCode(e.target.value)}>
                  {tiers.map(t => (
                    <option key={t.code} value={t.code}>
                      {t.displayName}{t.maxChildren !== null ? ` (up to ${t.maxChildren})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="no-status">Status</label>
                <select id="no-status" className="form-select" value={status} disabled={saving} onChange={e => setStatus(e.target.value as 'trial' | 'active')}>
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="no-period">Billing</label>
                <select id="no-period" className="form-select" value={billingPeriod} disabled={saving} onChange={e => setBillingPeriod(e.target.value as 'monthly' | 'annual')}>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
            </div>

            <h6 className="text-muted text-uppercase small mb-2">First admin</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="no-admin-name">Name</label>
                <input id="no-admin-name" className="form-control" maxLength={100} value={adminName} disabled={saving}
                  onChange={e => setAdminName(e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="no-admin-email">Email</label>
                <input id="no-admin-email" type="email" className="form-control" maxLength={100} value={adminEmail} disabled={saving}
                  onChange={e => setAdminEmail(e.target.value)} />
              </div>
            </div>
            <p className="form-text mt-2 mb-0">
              They get Admin, Editor and Reporting. A new address is sent an invitation with a temporary
              password; someone who already uses Rustle It keeps their sign-in and sees the new organisation next time.
            </p>
          </div>
          <div className="modal-footer">
            {missing && <span className="me-auto small text-muted">{missing}</span>}
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={create} disabled={!!missing || saving} title={missing ?? undefined}>
              {saving ? 'Creating…' : 'Create organisation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOrganisationModal;
